import React, {useState} from "react";
import Resources from "../Projects/sections/Project/sections/ProjectBoxComponent/Resources";
import {HiArrowLeft, HiLink} from "react-icons/hi";
import {DashboardStatus, getDashboardStatusColor, getDashboardStatusLabel,} from "../../constants/integrationTypes";
import TaskCompletionUpdateModal from "../Projects/sections/TaskTableModal";
import {ENTITY, resetDashboardDetails} from "../../redux/slices/timeline";
import {useDispatch, useSelector} from "react-redux";
import {useRoadmapService} from "../../services";
import {useParams} from "react-router-dom";
import {useJwt} from "../../helpers/common";
import {Badge} from "@mui/material";
import {getDefaultUserName} from "../../constants/defaultUsers";
import moment from "moment";
import {useResourceInfo} from "../../hooks/useResourceInfo";
import {hasAnyPositiveImpact, getPriorityColorClasses} from "../../utils/taskUtils";
import { getEntityTypeDisplay, pluralizeEntityType } from "../../utils/entityDisplayUtils";

interface ProjectDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: any;
  loading?: boolean;
}

const ProjectDetailsModalSkeleton = ({ userRole, showCost }: { userRole: string, showCost?: boolean }) => (
  <div className="flex flex-col h-full animate-pulse">
    {/* Header Skeleton */}
    <div className="flex items-center gap-4 px-8 py-4 rounded-b-2xl shadow-md z-20 border-b border-gray-200 dark:border-gray-700">
      <div className="flex flex-col flex-1">
        <div className="flex flex-row items-center gap-2 mb-2">
          <div className="w-8 h-8 bg-gray-300 dark:bg-gray-700 rounded-full" />
          <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-16" />
          <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-1/4" />
        </div>

        <div className="animate-pulse pl-9">
          <>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-2"></div>
            {/* Integration data skeleton */}
            <div className="flex flex-wrap gap-2 mt-1">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-12"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
            </div>
          </>
        </div>
      </div>
    </div>

    {/* Content Skeleton */}
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Stats Row Skeleton */}
      {userRole === "admin" && showCost && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="h-24 bg-gray-200 dark:bg-secondary-dark rounded-lg" />
          <div className="h-24 bg-gray-200 dark:bg-secondary-dark rounded-lg" />
          <div className="h-24 bg-gray-200 dark:bg-secondary-dark rounded-lg" />
        </div>
      )}

      {/* Status Card Skeleton */}
      <div className="h-28 bg-gray-200 dark:bg-secondary-dark rounded-lg" />

      {/* Tasks List Skeleton */}
      <div className="bg-gray-100 dark:bg-secondary-dark p-4 rounded-lg">
        <div className="h-5 bg-gray-300 dark:bg-gray-700 rounded w-1/3 mb-4" />
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg"
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ProjectDetailsModal: React.FC<ProjectDetailsModalProps> = ({
  isOpen,
  onClose,
  project,
  loading = false,
}) => {
  const jwtData: any = useJwt();
  const { org_role: userRole = "" } = jwtData;
  const [tasksToShowCount, setTasksToShowCount] = useState(6);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const { roadmap_id } = useParams();
  const dispatch = useDispatch();
  const { fetchSingleRoadmap, fetchData } = useRoadmapService();
  const { getResourceName } = useResourceInfo();

  const { show_cost: showCost } = useSelector((state: any) => state.organization.orgDetails);
  const { currentEntity, simulation } = useSelector((state: any) => state.timeline);

  const getIntegrationStatusBadgeClasses = (status?: string) => {
    const normalized = (status || "").toLowerCase();
    switch (normalized) {
      case "closed":
      case "done":
      case "resolved":
        return "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300 border border-green-200 dark:border-green-800";
      case "active":
      case "in progress":
      case "open":
        return "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 border border-blue-200 dark:border-blue-800";
      case "blocked":
      case "failed":
      case "canceled":
      case "cancelled":
        return "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800";
      default:
        return "bg-gray-50 text-gray-600 dark:bg-gray-800/40 dark:text-gray-300 border border-gray-200 dark:border-gray-700";
    }
  };

  const tasks = project.tasks || [];
  const teamMembers = project.people_allocated || [];
  const stats = project.stats || {};
  const projectCosts = project.project_costs || {};
  const simFlag = simulation;
  const tasksToShow = tasks.slice(0, tasksToShowCount);
  const hasPositiveImpact = React.useMemo(() => hasAnyPositiveImpact(tasks, simFlag), [tasks, simFlag]);

  const billing = Number(projectCosts.total_billing ?? 0);
  const totalCost = Number(projectCosts.total_cost ?? 0);
  const profit = billing - totalCost;
  const isProfit = profit >= 0;

  // Skeleton for loading tasks
  const TaskSkeleton = () => (
    <div className="flex items-center px-3 py-2 rounded-lg border border-light-border-default dark:border-gray-700 animate-pulse bg-light-surface dark:bg-[#232b3e]">
      <div className="w-2 h-2 rounded-full mr-3 bg-light-surfaceSecondary dark:bg-gray-700" />
      <div className="flex-1 h-4 bg-light-surfaceSecondary dark:bg-gray-700 rounded" />
    </div>
  );

  if (!isOpen || !project) return null;

  const handleCloseModal = () => {
    setShowEditTaskModal(false);
    setSelectedTask(null);
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop for closing modal on outside click */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div
        className={`relative w-full max-w-[95vw] md:max-w-[900px] h-full bg-white dark:bg-gray-900 shadow-2xl flex flex-col animate-fade-in ml-auto overflow-hidden`}
        style={{ transition: "opacity 0.3s" }}
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <ProjectDetailsModalSkeleton userRole={userRole} showCost={showCost} />
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-4 px-6 py-4 rounded-b-2xl shadow-md z-20 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-start flex-col">
                <div className="flex items-center gap-2">
                  <button
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors duration-200"
                    onClick={onClose}
                  >
                    <HiArrowLeft size={20} />
                  </button>
                  <span
                    className={`px-3 py-1 text-xs font-semibold rounded-lg ${getDashboardStatusColor(
                      project?.status
                    )}`}
                  >
                    {getDashboardStatusLabel(project?.status)}
                  </span>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                    {project?.name || "Project Details"}
                  </h2>
                </div>
                {project?.id && (
                  <div className="text-light-text-secondary dark:text-white/70 text-xs mt-0.5">
                    {project?.created_by ? (
                      <div className="pl-11">
                        Created by:{" "}
                        {getDefaultUserName(project?.created_by) ||
                          getResourceName(project?.created_by)}{" "}
                        on {moment(project?.created_at).format("ll")}
                      </div>
                    ) : project?.created_at ? (
                      <div className="pl-11">
                        Created on: {moment(project?.created_at).format("ll")}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Integration information */}
                {!loading && project?.integration_data && (
                  <div className="flex flex-wrap gap-2 mt-3 pl-11">
                    {project.integration_data.integration_type && (
                      <Badge
                        color="indigo"
                        className="px-3 py-1 text-xs rounded-md items-center justify-center flex font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 border-none"
                      >
                        {project?.integration_data.integration_type
                          .charAt(0)
                          .toUpperCase() +
                          project?.integration_data.integration_type
                            .slice(1)
                            .toLowerCase()}
                      </Badge>
                    )}
                    {project?.integration_data.integration_id && (
                      <Badge
                        color="info"
                        className="px-3 py-1 text-xs rounded-md items-center justify-center flex font-medium bg-light-status-info-bg text-light-status-info-text dark:bg-blue-900/30 dark:text-white/60 border-none"
                      >
                        <a
                          href={
                            project?.integration_data.integration_url ||
                            `https://${project?.integration_data.integration_type}.com/item/${project?.integration_data.integration_id}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <HiLink className="mr-1" />
                          {project?.integration_data.integration_id}
                        </a>
                      </Badge>
                    )}
                    {project?.integration_data.integration_item_type && (
                      <Badge
                        color="purple"
                        className="px-3 py-1 text-xs rounded-md items-center justify-center flex font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-none"
                      >
                        {project?.integration_data.integration_item_type
                          .charAt(0)
                          .toUpperCase() +
                          project?.integration_data.integration_item_type
                            .slice(1)
                            .toLowerCase()}
                      </Badge>
                    )}
                    {project?.integration_data.integration_status && (
                      <Badge
                        color="success"
                        className="px-3 py-1 text-xs rounded-md items-center justify-center flex font-medium bg-light-status-success-bg text-light-status-success-text dark:bg-green-900/30 dark:text-green-300 border-none"
                      >
                        <span className="mr-1 font-semibold">Status:</span>{" "}
                        {project?.integration_data.integration_status
                          .charAt(0)
                          .toUpperCase() +
                          project?.integration_data.integration_status
                            .slice(1)
                            .toLowerCase()}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
              {/* Stats Grid */}
              {userRole === "admin" && showCost && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Profit/Loss Card */}
                  <div
                    className={`p-4 rounded-lg border ${
                      isProfit
                        ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20"
                        : "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
                    }`}
                  >
                    <div
                      className={`text-sm font-medium ${
                        isProfit
                          ? "text-green-800 dark:text-green-300"
                          : "text-red-800 dark:text-red-300"
                      }`}
                    >
                      {isProfit ? "Profit" : "Loss"}
                    </div>
                    <div
                      className={`text-2xl font-bold mt-1 ${
                        isProfit
                          ? "text-green-900 dark:text-green-200"
                          : "text-red-900 dark:text-red-200"
                      }`}
                    >
                      $
                      {isProfit
                        ? profit.toLocaleString()
                        : Math.abs(profit).toLocaleString()}
                    </div>
                  </div>

                  {/* Cost & Billable Cards */}
                  <div className="p-4 rounded-lg bg-white dark:bg-secondary-dark border border-gray-200 dark:border-gray-700">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Cost
                    </div>
                    <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">
                      ${projectCosts.total_cost?.toLocaleString() ?? "0"}
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-white dark:bg-secondary-dark border border-gray-200 dark:border-gray-700">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Billable
                    </div>
                    <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">
                      ${projectCosts.total_billing?.toLocaleString() ?? "0"}
                    </div>
                  </div>
                </div>
              )}

              {/* Progress and Team */}
              <div className="grid grid-cols-12 gap-4 mb-6">
                {/* Overall Progress */}
                <div className="col-span-12 md:col-span-8 bg-white dark:bg-secondary-dark border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                  <div className="flex justify-between text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">
                    <span>Overall Progress</span>
                    <span className="font-bold text-gray-900 dark:text-white">
                      {project?.progress || 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${project?.progress || 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Team Members */}
                <div className="col-span-12 md:col-span-4 bg-white dark:bg-secondary-dark border border-gray-200 dark:border-gray-700 flex items-center justify-center p-4 rounded-lg">
                  <div className="flex items-center justify-center">
                    {project?.people_allocated &&
                    project.people_allocated.length > 0 ? (
                      <Resources
                        resources={project.people_allocated}
                        projectId={project?.id}
                      />
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        No members assigned
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Status Indicators Card */}
              <div className="p-4 rounded-lg bg-white dark:bg-secondary-dark border border-gray-200 dark:border-gray-700 mb-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                  {[
                    {
                      label: "Completed",
                      status: DashboardStatus.COMPLETED,
                      count: stats.completed,
                    },
                    // { label: 'Running', status: DashboardStatus.RUNNING, count: stats.running },
                    {
                      label: "Delayed",
                      status: DashboardStatus.DELAYED,
                      count: stats.delayed,
                    },
                    {
                      label: "On Track",
                      status: DashboardStatus.ON_TRACK,
                      count: stats.on_track,
                    },
                    {
                      label: "Closed",
                      status: DashboardStatus.CLOSED,
                      count: stats.closed,
                    },
                    {
                      label: "Unallocated",
                      status: DashboardStatus.UNALLOCATED,
                      count: stats.unallocated,
                    },
                    {
                      label: "Not Started",
                      status: DashboardStatus.NOT_STARTED,
                      count: stats.not_started,
                    },
                  ].map((item) => (
                    <div key={item.label} className="text-center">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {item.label}
                      </span>

                      <div className="flex items-center justify-center gap-2 mt-1">
                        <div
                          className={`w-2 h-2 rounded-full ${getDashboardStatusColor(
                            item.status
                          )}`}
                        ></div>
                        <div className="text-xl font-bold text-gray-900 dark:text-white">
                          {item.count || 0}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tasks/User Stories List */}
              <div className="bg-white dark:bg-secondary-dark border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                <h3 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  {(() => {
                      const { sublabel } = getEntityTypeDisplay(project?.entity_type, project?.type);
                      return pluralizeEntityType(2, sublabel || "Work Item"); // Force plural
                  })()}
                </h3>
                <div className="space-y-2">
                  {isTasksLoading
                    ? Array.from({ length: 4 }).map((_, idx) => (
                        <TaskSkeleton key={idx} />
                      ))
                    : tasksToShow.map((task: any, index: number) => (
                        <div
                          key={task.id || index}
                          className="relative flex items-center px-4 py-3 pr-[120px] rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 dark:bg-gray-700/50 dark:hover:bg-gray-700 transition-colors duration-200 border border-gray-200 dark:border-gray-700"
                          onClick={() => {
                            setSelectedTask(task);
                            setShowEditTaskModal(true);
                          }}
                        >
                          <span
                            className={`w-2 h-2 rounded-full mr-3 flex-shrink-0 ${
                              task.status
                                ? getDashboardStatusColor(task.status)
                                : "bg-gray-400"
                            }`}
                          ></span>
                          {Array.isArray(task.dependencies) &&
                            task.dependencies.length > 0 && (
                              <img
                                src={ hasPositiveImpact ? '/configuration%20red.svg' : '/configuration%20green.svg'}
                                alt="Dependency"
                                className="w-4 h-4 mr-2 inline-block"
                              />
                            // <FiLock className="text-red-500 dark:text-red-400 mr-2 flex-shrink-0" />
                            )}
                          <div className="flex items-center gap-3 w-full">
                            <div className="flex-1 min-w-0 pr-2">
                              <div
                                className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate"
                                title={task.title || task.name}
                              >
                                {task.title || task.name}
                              </div>
                              <div className="mt-1 flex items-center gap-2">
                                <div className="w-48 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-blue-600 dark:bg-blue-500"
                                    style={{
                                      width: `${Math.min(
                                        Math.max(Number(task.progress ?? 0), 0),
                                        100
                                      )}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                                  {Math.min(
                                    Math.max(Number(task.progress ?? 0), 0),
                                    100
                                  )}
                                  %
                                </span>
                                {task?.priority && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${getPriorityColorClasses(task.priority)}`}>
                                    P{task.priority}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="absolute right-4 top-1/2 -translate-y-1/2 w-[100px] text-right">
                            {(task?.integration_data?.integration_status ||
                              task?.integration_status) && (
                              <span
                                className={`inline-block text-[10px] px-2 py-0.5 rounded-full capitalize whitespace-nowrap ${getIntegrationStatusBadgeClasses(
                                  task?.integration_data?.integration_status ||
                                    task?.integration_status
                                )}`}
                                title={`Integration status: ${
                                  task?.integration_data?.integration_status ||
                                  task?.integration_status
                                }`}
                              >
                                {task?.integration_data?.integration_status ||
                                  task?.integration_status}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                </div>
                {!isTasksLoading && tasksToShowCount < tasks.length && (
                  <button
                    className="mt-4 w-full text-center text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 py-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-200"
                    onClick={() =>
                      setTasksToShowCount((count) =>
                        Math.min(count + 10, tasks.length)
                      )
                    }
                  >
                    View more
                  </button>
                )}
                {!isTasksLoading &&
                  tasksToShowCount >= tasks.length &&
                  tasks.length > 6 && (
                    <button
                      className="mt-4 w-full text-center text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 py-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-200"
                      onClick={() => setTasksToShowCount(6)}
                    >
                      Show less
                    </button>
                  )}
              </div>
            </div>

            {showEditTaskModal && selectedTask && (
              <TaskCompletionUpdateModal
                project_id={project?.id}
                isVisiable={showEditTaskModal}
                handleAddNewTask={() => {
                  setShowEditTaskModal(false);
                  setSelectedTask(null);
                }}
                onClose={handleCloseModal}
                onTaskUpdate={() => {
                  // Refresh data after task update
                  if (currentEntity === ENTITY.ROADMAP) {
                    dispatch(resetDashboardDetails());
                    fetchData(project?.id, simulation, {}, true).then(() => {
                      fetchSingleRoadmap(roadmap_id, false);
                    });
                  }
                  setShowEditTaskModal(false);
                  setSelectedTask(null);
                  onClose();
                }}
                tasks={tasks}
                selectedTask={selectedTask}
                members={teamMembers}
                sim_flag={false}
              />
            )}
          </>
        )}
      </div>
      <style>
        {`
          .animate-fade-in {
            opacity: 0;
            animation: fadeIn 0.3s forwards;
          }
          @keyframes fadeIn {
            to {
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ProjectDetailsModal;
